/**
 * CardsDueTodayWidget — replaces the vague NextActionHero with a
 * specific finite count. The Wordle / NYT Mini effect: clear daily task
 * → satisfying completion → return tomorrow.
 *
 * "12 cards due · ~6 min" beats "got 15 minutes?" every time.
 */
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Sparkles, Check } from 'lucide-react';
import api, { apiUrl } from '../utils/apiClient';

const SECONDS_PER_CARD = 30; // generous estimate for a SM-2 review

export default function CardsDueTodayWidget() {
  // TanStack Query — shared cache across the dashboard (the Progress tab
  // also reads /srs/stats via DueReviewsWidget). 60s staleTime means
  // navigating between tabs is instant; refetch fires on window focus.
  const { data: stats } = useQuery({
    queryKey: ['srs-stats'],
    queryFn: async () => (await api.get(apiUrl('/srs/stats'))).data,
    staleTime: 60_000,
  });

  if (!stats) return null;

  const due = stats.due_now || 0;

  // Caught-up state — celebrate the empty inbox.
  if (due === 0) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
        className="border border-[var(--isp-glass-border)] bg-[var(--isp-glass)] p-5"
        data-testid="cards-due-widget-empty"
      >
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 flex items-center justify-center bg-emerald-500/10 shrink-0">
            <Check className="w-5 h-5 text-emerald-500" />
          </div>
          <div className="min-w-0">
            <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-emerald-500 mb-0.5">All caught up</p>
            <p className="text-sm font-bold text-[var(--isp-heading)] tracking-tight">No cards due today.</p>
            <p className="text-xs text-[var(--isp-muted)]">Nice work &mdash; your future self will thank you.</p>
          </div>
        </div>
      </motion.div>
    );
  }

  const estMinutes = Math.max(1, Math.round((due * SECONDS_PER_CARD) / 60));

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
      data-testid="cards-due-widget"
    >
      <Link
        to="/review"
        className="block border border-[var(--isp-glass-border)] hover:border-[#002FA7] bg-[var(--isp-glass)] hover:bg-[var(--isp-glass-hover)] p-5 transition-colors group"
      >
        <div className="flex items-center gap-4">
          <div className="w-11 h-11 flex items-center justify-center bg-[#002FA7]/10 group-hover:bg-[#002FA7] transition-colors shrink-0">
            <Sparkles className="w-5 h-5 text-[#002FA7] group-hover:text-white transition-colors" />
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-[#002FA7] mb-1">Today&apos;s review</p>
            <h3 className="text-base font-bold text-[var(--isp-heading)] tracking-tight">
              <span className="text-[#002FA7]" data-testid="cards-due-count">{due}</span>{' '}
              card{due === 1 ? '' : 's'} due · ~{estMinutes} min
            </h3>
            <p className="text-xs text-[var(--isp-muted)] mt-0.5">Quick spaced-repetition pass — keep your streak alive.</p>
          </div>
          <ArrowRight className="w-4 h-4 text-[var(--isp-muted)] group-hover:text-[var(--isp-heading)] group-hover:translate-x-1 transition-all" />
        </div>
      </Link>
    </motion.div>
  );
}
